import React, { useEffect, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';

import { ReactComponent as HappyBuddy } from '../../../../assets/icons/v3/happy-buddy.svg';
import { CLOSE_ICON } from '../../../constants/images';
import { CAN_SEE_HELP_US_IMPROVE_FG, HELP_US_IMPROVE_SCHEDULE_CONFIG } from '../../../constants/statsig';
import { SCHEDULE_INTERVIEW, SCHEDULE_INTERVIEW_CANCEL, CLICK_ACTION } from '../../../constants/tagManager';
import { useFeatureGate } from '../../../hooks/useFeatureGate/useFeatureGate';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_HELP_US_IMPROVE_BANNER } from '../../../translations/helpUsImproveBanner';
import BdsButton from '../Buttons/BdsButton/BdsButton';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';

import './HelpUsImproveBanner.scss';

const SESSION_PREFIX = 'help_us_improve_banner_dismissed_';

const clearBannerSessionOnBeforeUnload = () => {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith(SESSION_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
};

const BEFORE_UNLOAD_FLAG = '__helpUsImproveBannerBeforeUnloadListener__';

if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown>;
  if (!w[BEFORE_UNLOAD_FLAG]) {
    window.addEventListener('beforeunload', clearBannerSessionOnBeforeUnload);
    w[BEFORE_UNLOAD_FLAG] = true;
  }
}

interface HelpUsImproveBannerProps {
  pageKey?: string;
}

const HelpUsImproveBanner: React.FC<HelpUsImproveBannerProps> = ({ pageKey = 'default' }) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const sessionKey = `${SESSION_PREFIX}${pageKey}`;
  const { isEnabled } = useFeatureGate(CAN_SEE_HELP_US_IMPROVE_FG);
  const { client } = useStatsigClient();

  const scheduleUrl = client
    .getDynamicConfig(HELP_US_IMPROVE_SCHEDULE_CONFIG.name)
    .get(
      HELP_US_IMPROVE_SCHEDULE_CONFIG.parameters.url.name,
      HELP_US_IMPROVE_SCHEDULE_CONFIG.parameters.url.defaultValue
    );

  useEffect(() => {
    const isDismissedInSession = sessionStorage.getItem(sessionKey);
    if (!isDismissedInSession && isEnabled) {
      setShowBanner(true);
    }
  }, [sessionKey, isEnabled]);

  const closeBanner = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SCHEDULE_INTERVIEW_CANCEL, { screen: pageKey });
    sessionStorage.setItem(sessionKey, 'true');
    setShowBanner(false);
  };

  const openSchedule = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SCHEDULE_INTERVIEW, { screen: pageKey });
    window.open(scheduleUrl, '_blank', 'noopener,noreferrer');
  };

  if (!isEnabled || !showBanner) {
    return null;
  }

  return (
    <div className="help-us-improve-banner">
      <HappyBuddy className="help-us-improve-banner-buddy" aria-hidden focusable={false} />
      <div className="help-us-improve-banner-text">
        <div className="help-us-improve-banner-title">{t(TRANSLATIONS_HELP_US_IMPROVE_BANNER.title)}</div>
        <div className="help-us-improve-banner-description">{t(TRANSLATIONS_HELP_US_IMPROVE_BANNER.description)}</div>
      </div>
      <div className="help-us-improve-banner-actions">
        <BdsButton variant="primary" size="s" action={openSchedule}>
          {t(TRANSLATIONS_HELP_US_IMPROVE_BANNER.button)}
        </BdsButton>
        <BdsIconButton
          icon={CLOSE_ICON}
          variant="transparent"
          size="sm"
          label={t(TRANSLATIONS_HELP_US_IMPROVE_BANNER.close)}
          action={closeBanner}
        />
      </div>
    </div>
  );
};

export default HelpUsImproveBanner;
